import { ClipboardCheck, Layers, HandHeart } from "lucide-react";

/**
 * WhatAStudyLooksLike , a concrete 3-step explainer that does NOT assume
 * every study is a Zoom interview. Communicates the variety of research
 * formats and frames compensation as a thank-you, not the headline.
 */
export const WhatAStudyLooksLike = () => {
  const steps = [
    {
      icon: ClipboardCheck,
      kicker: "Step 1",
      title: "Tell us a little about you",
      body:
        "Answer a short 5-minute screener about your condition or who you care for. It helps us make sure each study is genuinely relevant to your experience, and we only get in touch when there's a real fit.",
    },
    {
      icon: Layers,
      kicker: "Step 2",
      title: "Take part in your way",
      body:
        "Studies come in many formats — for example quick surveys, Zoom interviews, discussion boards, daily diaries or wearable studies. We pick what fits the study and your life.",
    },
    {
      icon: HandHeart,
      kicker: "Step 3",
      title: "Be heard, and thanked for it",
      body:
        "Your insight goes straight to the teams shaping treatments and care. We send a fair thank-you for your time, usually within 48 hours, by your choice of wire transfer, cash cards or eGift cards.",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="bg-white"
      data-testid="what-a-study-looks-like"
    >
      <div className="container-main py-16 md:py-24">
        <div className="max-w-2xl mb-12">
          <span className="badge badge-light mb-4">How it works</span>
          <h2 className="font-['Outfit'] font-bold text-[var(--navy)] tracking-tight text-3xl md:text-4xl lg:text-5xl mb-4">
            What taking part actually looks like.
          </h2>
          <p className="text-[var(--text-muted)] text-lg leading-relaxed">
            No clinics. No trials. No treatments. Just an honest conversation,
            on the format that fits you, about life with your condition.
          </p>
        </div>

        <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
          {/* Connector line (desktop only) */}
          <span
            aria-hidden="true"
            className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px border-t border-dashed border-[var(--light-border)]"
          />

          {steps.map(({ icon: Icon, kicker, title, body }, i) => (
            <li
              key={kicker}
              className="relative bg-white border border-[var(--light-border)] rounded-2xl p-7 hover:border-[var(--teal)] hover:shadow-[0_12px_30px_-15px_rgba(5,150,105,0.25)] transition-all"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="w-14 h-14 rounded-2xl bg-[var(--teal-soft)] text-[var(--teal-dark)] flex items-center justify-center">
                  <Icon size={26} strokeWidth={1.8} />
                </span>
                <span className="font-['Outfit'] font-bold text-[var(--navy)]/10 text-5xl leading-none">
                  0{i + 1}
                </span>
              </div>
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[var(--teal-dark)] mb-2">
                {kicker}
              </p>
              <h3 className="font-['Outfit'] font-semibold text-[var(--navy)] text-xl mb-2 leading-tight">
                {title}
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed text-[15px]">
                {body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
